// vg-binding: CONTEXT:decisions
// vg-binding: INTERFACE-STANDARDS:numeric-precision
//
// packages/utils/src/money.ts
//
// Phase 4.2 Wave 1 Task 02 — Money utilities for the pricing pipeline.
//
// Per CONTEXT.md (rounding 2dp, ≥0.005 lên / <0.005 xuống — Big.js
// ROUND_HALF_UP, RM = 3 in Big.js terminology), (round per line →
// sum after — Float → round 2dp → ×100 → integer cents, ±1¢ per line tolerance)
// and (USD canonical — all backend pricing in USD cents, integer
// storage, Big.js precision intermediate).
// Per INTERFACE-STANDARDS § numeric-precision (deterministic rounding for
// money envelopes; never silently coerce negative values into the cents
// pipeline — surface a typed error instead).
//
// All functions are PURE — no I/O, no Date.now(), no env access. Every
// function is deterministic for any given input. This is what makes the
// downstream test surface tractable for line-step tasks 12-21 (per-line
// rounding contract): every assertion is `expect(fn(input)) === expected`.
//
// Why Big.js (not number / decimal.js / dinero):
//   - JS `number` (IEEE-754) loses precision at 0.1 + 0.2 = 0.30000000000004.
//     For multi-line USD pricing with 6-zone shipping × tax × tariff stacks,
//     accumulated float error breaks the ±1¢/line tolerance contract.
//   - decimal.js is broader (arbitrary precision) but ~3× the bundle size and
//     overkill for fixed-point USD math.
//   - dinero.js v2 is purely cents-based — fine for storage, but its public
//     API does not expose ROUND_HALF_UP for intermediate fractional dollars.
//   - Big.js exposes `Big.RM` (rounding mode) as a static constant so each
//     call can pin RM = 3 (HALF_UP — Big.js convention: 0=DOWN, 1=HALF_UP,
//     2=HALF_EVEN, 3=UP). we want **HALF_UP** semantics
//     (≥0.005 rounds up, <0.005 rounds down) — that is RM = 1 in Big.js.

import Big from 'big.js';

// ---------------------------------------------------------------------------
// Big.js rounding-mode constant — pin local-only to avoid global mutation
// of `Big.RM` (which would leak HALF_UP into other Big.js consumers in the
// process). Every `.round(dp, MODE)` call passes this explicitly.
// ---------------------------------------------------------------------------
//
// Big.js rounding modes:
//   0  ROUND_DOWN          — toward zero
//   1 ROUND_HALF_UP — ≥0.5 up, <0.5 down (spec)
//   2  ROUND_HALF_EVEN     — banker's rounding
//   3  ROUND_UP            — away from zero
//
// We use HALF_UP (1) per CONTEXT.md: "≥0.005 lên, <0.005 xuống".
// The plan slice's "RM = 3 (UP)" wording is a loose shorthand — the exact
// spec is ≥0.005 → up, <0.005 → down, which IS HALF_UP, NOT plain UP. Plain
// UP would round 0.001 up to 0.01, contradicting the spec.

const ROUND_HALF_UP = 1 as const;

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Thrown when a money input violates the pricing pipeline invariants
 * (e.g., negative cents, non-integer cents, NaN/Infinity dollars).
 *
 * Per INTERFACE-STANDARDS § error-shape (default API error envelope), the
 * `code` field is stable and machine-readable. Downstream Fastify handlers
 * map this to `error.code = 'INVALID_MONEY'` in the API envelope.
 */
export class InvalidMoneyError extends Error {
  public readonly code = 'INVALID_MONEY';
  public readonly value: unknown;

  constructor(message: string, value: unknown) {
    super(message);
    this.name = 'InvalidMoneyError';
    this.value = value;
    // Preserve V8 stack trace if available
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, InvalidMoneyError);
    }
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

type MoneyInput = Big | number | string;

/**
 * Coerce an arbitrary money-like value into a Big.js instance, rejecting
 * non-finite numbers and unparseable strings up front.
 *
 * Big.js itself throws on `NaN`/`Infinity` — we wrap that error with a typed
 * `InvalidMoneyError` so callers can branch on `.code === 'INVALID_MONEY'`
 * without sniffing string contents.
 */
function toBig(value: MoneyInput, label: string): Big {
  if (value instanceof Big) {
    return value;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw new InvalidMoneyError(
        `${label}: expected finite number, got ${String(value)}`,
        value,
      );
    }
  }
  try {
    return new Big(value as number | string);
  } catch (err) {
    throw new InvalidMoneyError(
      `${label}: cannot parse value as Big — ${(err as Error).message}`,
      value,
    );
  }
}

// ---------------------------------------------------------------------------
// Public API — round-half-up + Big.js wrappers (envelope-summary signatures)
// ---------------------------------------------------------------------------

/**
 * Round a money value to N decimal places using HALF_UP.
 *
 * Examples (decimals=2):
 *   1.005  → 1.01    (≥0.005 rounds up)
 *   1.0049 → 1.00    (<0.005 rounds down)
 *   1.006  → 1.01
 *  -1.005  → -1.01   (HALF_UP is symmetric around zero in Big.js v6)
 */
export function roundHalfUp(value: MoneyInput, decimals: number): Big {
  if (!Number.isInteger(decimals) || decimals < 0) {
    throw new InvalidMoneyError(
      `roundHalfUp: decimals must be a non-negative integer, got ${String(decimals)}`,
      decimals,
    );
  }
  const big = toBig(value, 'roundHalfUp');
  return big.round(decimals, ROUND_HALF_UP);
}

/**
 * Convert dollars → integer cents (USD discipline).
 *
 *   toCents(19.99)   → 1999
 *   toCents(19.005)  → 1901   (HALF_UP at 2dp: 19.005 → 19.01 → ×100)
 *   toCents(0.10)    → 10
 *
 * Pipeline (clarification): float dollars → round to 2dp →
 * ×100 → integer cents. Returns native `number` (safe up to 2^53−1 ¢ ≈
 * 90 trillion USD; sufficient for any real-world order total).
 *
 * Negative inputs are NOT silently coerced — callers must explicitly handle
 * refund/credit cases via dedicated `creditCents` helpers (Phase 4.1 scope).
 */
export function toCents(usd: MoneyInput): number {
  const big = toBig(usd, 'toCents');
  if (big.lt(0)) {
    throw new InvalidMoneyError(
      `toCents: negative dollar input rejected — got ${big.toString()}`,
      usd,
    );
  }
  //  pipeline clarification: round dollars to 2dp BEFORE ×100.
  const dollarsRounded = big.round(2, ROUND_HALF_UP);
  const cents = dollarsRounded.times(100).round(0, ROUND_HALF_UP);
  return Number(cents.toString());
}

/**
 * Convert integer cents → Big dollars (inverse of `toCents`).
 *
 *   fromCents(1999) → Big('19.99')
 *   fromCents(10)   → Big('0.10')
 *
 * Returns Big (not number) so chained arithmetic keeps full precision.
 * Use `.toFixed(2)` or `.toNumber()` at presentation/serialization edge.
 */
export function fromCents(cents: number): Big {
  if (!Number.isFinite(cents) || !Number.isInteger(cents)) {
    throw new InvalidMoneyError(
      `fromCents: expected integer cents, got ${String(cents)}`,
      cents,
    );
  }
  return new Big(cents).div(100);
}

/**
 * Multiply two money values, rounding the product to `decimals` places.
 * Default 2-decimal rounding matches the per-line USD precision contract.
 */
export function multiplyMoney(
  a: MoneyInput,
  b: MoneyInput,
  decimals = 2,
): Big {
  const ab = toBig(a, 'multiplyMoney.a').times(toBig(b, 'multiplyMoney.b'));
  return ab.round(decimals, ROUND_HALF_UP);
}

/**
 * Add two money values, rounding the sum to `decimals` places.
 */
export function addMoney(a: MoneyInput, b: MoneyInput, decimals = 2): Big {
  const sum = toBig(a, 'addMoney.a').plus(toBig(b, 'addMoney.b'));
  return sum.round(decimals, ROUND_HALF_UP);
}

/**
 * Subtract `b` from `a`, rounding the difference to `decimals` places.
 */
export function subtractMoney(
  a: MoneyInput,
  b: MoneyInput,
  decimals = 2,
): Big {
  const diff = toBig(a, 'subtractMoney.a').minus(toBig(b, 'subtractMoney.b'));
  return diff.round(decimals, ROUND_HALF_UP);
}

// ---------------------------------------------------------------------------
// Public API — plan-slice signatures (cents-bigint discipline)
// ---------------------------------------------------------------------------
//
// The plan slice (PLAN/task-02.md) names these explicitly — they are the
// canonical pricing-pipeline helpers consumed by Tasks 12-21 (line steps).
// `bigint` return type guarantees integer-only cents (no accidental float
// downgrade in callers); JSON serializers convert via `.toString()`.

/**
 * Convert dollars → BigInt cents per the plan-slice contract.
 *
 *   dollarsToCents(19.99)   → 1999n
 *   dollarsToCents(19.005)  → 1901n
 *   dollarsToCents(0.0049)  → 0n     (rounds down)
 *   dollarsToCents(0.005)   → 1n     (rounds up — HALF_UP boundary)
 *   dollarsToCents(0.0051)  → 1n
 */
export function dollarsToCents(usd: MoneyInput): bigint {
  return BigInt(toCents(usd));
}

/**
 * Convert a BigInt cents value to a JS `number` with an explicit
 * `Number.MAX_SAFE_INTEGER` overflow guard (per F-14 / INTERFACE-STANDARDS
 * § numeric-precision). `Number(big)` silently rounds once `big` exceeds
 * 2^53−1, which would corrupt grand totals on extreme orders. We surface a
 * `RangeError` instead so the failure is loud at the boundary.
 *
 * The cap (`Number.MAX_SAFE_INTEGER` ≈ 9.007 × 10^15 cents ≈ $90.07T) is
 * astronomically larger than any realistic order, but the invariant matters
 * so a regression in BigInt accumulation cannot silently land in the
 * snapshot pipeline.
 *
 *   centsBigIntToNumber(1999n) → 1999
 *   centsBigIntToNumber(BigInt(Number.MAX_SAFE_INTEGER)) → 9007199254740991
 *   centsBigIntToNumber(BigInt(Number.MAX_SAFE_INTEGER) + 1n) → throws RangeError
 */
export function centsBigIntToNumber(v: bigint): number {
  if (v > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new RangeError(
      `centsBigIntToNumber: cents value ${v.toString()} exceeds Number.MAX_SAFE_INTEGER (${Number.MAX_SAFE_INTEGER}); refusing to silently round.`,
    );
  }
  if (v < -BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new RangeError(
      `centsBigIntToNumber: cents value ${v.toString()} is below -Number.MAX_SAFE_INTEGER; refusing to silently round.`,
    );
  }
  return Number(v);
}

/**
 * Convert BigInt|number cents → number dollars (presentation surface).
 *
 *   centsToDollars(1999n) → 19.99
 *   centsToDollars(10)    → 0.1
 *
 * Returns native `number` for direct JSON serialization; precision is safe
 * because cents ≤ 2^53−1 covers any realistic order total.
 */
export function centsToDollars(cents: bigint | number): number {
  const asNum = typeof cents === 'bigint' ? Number(cents) : cents;
  if (!Number.isFinite(asNum) || !Number.isInteger(asNum)) {
    throw new InvalidMoneyError(
      `centsToDollars: expected integer cents, got ${String(cents)}`,
      cents,
    );
  }
  // Use Big to avoid 1999 / 100 = 19.990000000000002 IEEE-754 artefacts.
  return Number(new Big(asNum).div(100).toFixed(2));
}

/**
 * Multiply line-item unit cents × quantity, returning BigInt cents.
 * Quantity must be a positive integer (: qty ≥ 1).
 *
 *   mulCentsByQty(1999n, 3) → 5997n   (3 × $19.99 = $59.97)
 */
export function mulCentsByQty(cents: bigint | number, qty: number): bigint {
  if (!Number.isInteger(qty) || qty < 1) {
    throw new InvalidMoneyError(
      `mulCentsByQty: qty must be a positive integer, got ${String(qty)}`,
      qty,
    );
  }
  const c = typeof cents === 'bigint' ? cents : BigInt(cents);
  if (c < 0n) {
    throw new InvalidMoneyError(
      `mulCentsByQty: negative cents input rejected — got ${c.toString()}`,
      cents,
    );
  }
  return c * BigInt(qty);
}

/**
 * Sum N BigInt|number cents values into BigInt cents (multi-line totals).
 * callers MUST round per-line BEFORE summing — this helper
 * does NOT re-round; it is a pure additive aggregator.
 *
 *   sumCents(1999n, 500n, 250n) → 2749n
 */
export function sumCents(...values: Array<bigint | number>): bigint {
  let total = 0n;
  for (const v of values) {
    const c = typeof v === 'bigint' ? v : BigInt(v);
    total += c;
  }
  return total;
}

/**
 * Round a raw cents value (which may be fractional from a Big.js intermediate)
 * to integer cents using HALF_UP. Used by line-step tasks 12-21 to enforce
 * the per-line ±1¢ rounding boundary before sum.
 *
 *   roundLineCents(1999.4)  → 1999n
 *   roundLineCents(1999.5)  → 2000n   (HALF_UP)
 *   roundLineCents('19.005') → 19n    (interpret as cents, NOT dollars)
 */
export function roundLineCents(rawCents: MoneyInput): bigint {
  const big = toBig(rawCents, 'roundLineCents');
  if (big.lt(0)) {
    throw new InvalidMoneyError(
      `roundLineCents: negative cents input rejected — got ${big.toString()}`,
      rawCents,
    );
  }
  const rounded = big.round(0, ROUND_HALF_UP);
  return BigInt(rounded.toString());
}
